import { useModalStatusMessage } from '@/hooks';
import { useGlobalModal } from '@/hooks/useGlobalModal';
import { useMutation } from '@apollo/client';
import { DateTimePickerController } from '@components/controllers/DateTimePickerController';
import { TextFieldController } from '@components/controllers/TextFieldController';
import GlobalModal from '@components/GlobalModal';
import ModalActions from '@components/modal/ModalActions';
import ModalContent from '@components/modal/ModalContent';
import ModalTitle from '@components/modal/ModalTitle';
import { Access, AuthorizationWrapper } from '@features/authorization';
import StandardAutocompleteIdsWithFallback from '@features/standardDesign/components/autocomplete/StandardAutocompleteIds';
import { zodResolver } from '@hookform/resolvers/zod';
import { type I18n } from '@lingui/core';
import { msg, t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { LocalizationProvider } from '@mui/x-date-pickers-latest';
import { AdapterDayjs } from '@mui/x-date-pickers-latest/AdapterDayjs';
import dayjs from 'dayjs';
import { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod/v4';
import CREATE_DEVICE_REPORT from '../graphql/mutations/createDeviceReport';

interface Props {
  defaultDeviceId?: string;
  selectableDeviceIds?: string[];
  onClose: () => void;
}

const ValidationSchema = (i18n: I18n) =>
  z.object({
    deviceId: z.string(),
    messageReport: z.string().min(1, t(i18n)`Este campo no puede estar vacío`),
    dateReport: z.custom<dayjs.Dayjs>((val) => val instanceof dayjs, t(i18n)`Fecha inválida`),
  });

type FormValues = z.output<ReturnType<typeof ValidationSchema>>;

const getDefaultValues = (deviceId: string | undefined) => ({
  deviceId,
  messageReport: '',
  dateReport: dayjs(),
});

const SuspenseComponent: FC<Props> = ({ defaultDeviceId, selectableDeviceIds, onClose }) => {
  const { _, i18n } = useLingui();
  const { openModalErrorMessage, openModalSuccessMessage } = useModalStatusMessage();

  const { handleSubmit, control, formState, reset } = useForm<FormValues>({
    defaultValues: getDefaultValues(defaultDeviceId),
    resolver: zodResolver(ValidationSchema(i18n)),
  });

  const [createDeviceReport] = useMutation(CREATE_DEVICE_REPORT, {
    onCompleted() {
      openModalSuccessMessage(
        _(msg`Reporte enviado correctamente. Espera unos minutos para poder visualizar el evento`),
      );
      reset();
      onClose();
    },
    onError() {
      openModalErrorMessage(_(msg`Error al enviar el reporte`));
    },
  });

  const onSubmit = handleSubmit(async (data) => {
    await createDeviceReport({
      variables: {
        input: {
          deviceId: data.deviceId,
          message: data.messageReport,
          manualDate: data.dateReport.millisecond(),
        },
      },
    });
  });

  return (
    <>
      <ModalTitle onClose={onClose} title={_(msg`Reportar un problema`)} />
      <form onSubmit={onSubmit}>
        <ModalContent sx={{ pt: 4 }}>
          <Controller
            control={control}
            name="deviceId"
            render={({ field, fieldState }) => (
              <StandardAutocompleteIdsWithFallback
                disabled={field.disabled}
                dataType="devices"
                value={field.value}
                onChange={(_, values) => field.onChange(values)}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                sx={{ mb: 2 }}
                queryOptions={{
                  devices: {
                    ids: selectableDeviceIds,
                  },
                }}
              />
            )}
          />
          <TextFieldController
            control={control}
            name="messageReport"
            label={_(msg`Evento a reportar`)}
            slotProps={{
              textField: {
                fullWidth: true,
              },
            }}
          />

          {/* TODO: Create a proper access attribute */}
          <AuthorizationWrapper access={Access.ShowAuditAdminUsers} fallback={null}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateTimePickerController
                control={control}
                name="dateReport"
                label={_(msg`Fecha del reporte`)}
                ampm
                sx={{ mt: 2 }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                  },
                }}
              />
            </LocalizationProvider>
          </AuthorizationWrapper>
        </ModalContent>
        <ModalActions
          onClose={onClose}
          onSubmit="submit"
          submitLabel={_(msg`Enviar`)}
          submitLoading={formState.isSubmitting}
        />
      </form>
    </>
  );
};

export const ReportProblemModal: FC = () => {
  const { globalModalProps, state, onClose } = useGlobalModal('reportProblem');

  return (
    <GlobalModal {...globalModalProps} dialogProps={{ maxWidth: 'md' }}>
      {state.open && (
        <>
          <SuspenseComponent
            defaultDeviceId={state.defaultDeviceId}
            selectableDeviceIds={state.selectableDeviceIds}
            onClose={onClose}
          />
        </>
      )}
    </GlobalModal>
  );
};
